import { createApi } from '@reduxjs/toolkit/query/react';
import { generateClient } from 'aws-amplify/api';

// Dashboard GraphQL strings
import {
  getCommitteeListQuery,
  getInProgressQuery,
  getNextAppQuery,
  getSystemWideMessagesQuery,
} from '../../Dashboard/query';
import { dismissSystemWideMessagesMutation } from '../../Dashboard/mutation';

// Dashboard transform helpers
import { transformGetNextApp, filterInProgressByAdminAction } from '../../Dashboard/transform';

// Applicant GraphQL strings
import {
  getApplication,
  getApplicationReadOnlyQuery,
  getTraining,
  searchApplicationDocuments,
  searchApplicationDocumentsReadOnlyQuery,
  getApplicationDocument,
  getApplicationDocumentReadOnly,
  getApplicationTrainingDocument,
  getCommitteeReviewers,
  getExpectedRecommendationQuery,
  getTrainingConfigQuery,
} from '../../Applicant/query';
import {
  saveReviewMutation,
  submitReviewMutation,
  submitTrainingMutation,
  overrideRecommendationMutation,
  createTrainingMutation,
  updatePriorityMutation,
  editTrainingMutation,
  unassignTraineeMutation,
  assignApplicationMutation,
  unassignApplicationMutation,
  reportConflictMutation,
  restartEvaluationMutation,
  notifyAdminMutation,
  reassignApplicationMutation,
  clearNotifyAdminMutation,
  holdForExportMutation,
  holdForAdminMutation,
} from '../../Applicant/mutation';

// Applicant transform helpers
import {
  transformSchemaToComponent,
  transformDocumentList,
  transformReviewer,
} from '../../Applicant/transform';

// ManageApplications GraphQL strings
import {
  getManageApplicationsDataQuery,
  listAssignableApplicationsQuery,
  listAssignedApplicationsQuery,
  getReviewerDetailsByCommitteeQuery,
  getCommitteeDetailsQuery,
} from '../../ManageApplications/query';
import {
  assignApplicationsMutation,
  unassignApplicationsMutation,
  prioritizeApplicationsBulkMutation,
} from '../../ManageApplications/mutation';

// AssignDrawer GraphQL strings
import { getReviewerDetailsByIdQuery } from '../../AssignDrawer/query';

// ManageReviewer GraphQL strings
import { getManageReviewerQuery } from '../../ManageReviewer/query';
import { updateManageReviewerMutation } from '../../ManageReviewer/mutation';

// ManageReviewersBulkAssign GraphQL strings
import { getCommitteeReviewersFromDB as getCommitteeReviewersFromDBQuery } from '../../ManageReviewersBulkAssign/query';
import { updateManageReviewerMutation as bulkAssignReviewersMutation } from '../../ManageReviewersBulkAssign/mutation';

// ManageActiveReviewers GraphQL strings
import { getCommitteeReviewersFromDB as getActiveReviewersQuery } from '../../ManageActiveReviewers/query';
import { activateReviewersMutation } from '../../ManageActiveReviewers/mutation';

// ManageAdmissionsCycles GraphQL strings
import { listAdmissionsCyclesQuery } from '../../ManageAdmissionsCycles/query';
import { saveNewAdmissionsCycleMutation } from '../../ManageAdmissionsCycles/mutation';

// Navbar GraphQL strings
import { searchCommitteeReviewers } from '../../Navbar/query';

// AuditLookup GraphQL strings
import { getSearchResultsQuery } from '../../AuditLookup/query';

// Common Reports GraphQL strings
import { listCustomFiltersQuery } from '../../Common/components/reports/query';

// ReportsDash GraphQL strings
import { listCustomReportsQuery } from '../../ReportsDash/query';

// ReportAthenaCustom GraphQL strings
import { getCustomReportDataQuery } from '../../ReportAthenaCustom/query';

// ReportsExports GraphQL strings
import { listExportsForCommitteeIdQuery, listExportsForCommitteeIdsQuery, getDownloadableLinkForPathsQuery } from '../../ReportsExports/query';
import { saveExportTaskCompleteMutation } from '../../ReportsExports/mutation';

// ReportAthenaCustom GraphQL strings
import { saveReviewNotesAddressedMutation, getReviewNotesAddressedStatusQuery } from '../../ReportAthenaCustom/mutation';

// ReportAdminReviews GraphQL strings
import { exportAdminReviewsReportApplicationsMutation } from '../../ReportAdminReviews/mutation';

// ReportRecommendations GraphQL strings
import { exportRecommendationsReportsMutation } from '../../ReportRecommendations/mutation';

// OpenSearch search GraphQL strings
import { SEARCH_REPORT as searchReportQuery, EXPORT_SEARCH_REPORT as exportSearchReportMutation, RESOLVE_SELECTED_IDS as resolveSelectedIdsQuery } from '../../Common/components/OpenSearchDataGrid/graphql';

// AdvancedSearch GraphQL strings
import { submitBulkAdminRecommendationMutation } from '../../AdvancedSearch/mutation';
import { getCommitteeRecommendationOptionsQuery } from '../../AdvancedSearch/query';

let client = null;

const graphqlBaseQuery = async ({ query, variables = {} }) => {
  if (!client) {
    client = generateClient();
  }
  try {
    const result = await client.graphql({ query, variables });
    return { data: result.data };
  } catch (error) {
    // Amplify v6 throws on partial errors (e.g. field-level auth failures) but
    // still includes the successful data in error.data. Only treat this as a
    // (partial) success when error.data actually contains usable, non-null data
    // to render. When every requested field is null and an errors array is
    // present, this is a genuine failure and must surface as an error so callers
    // (and the global AlertBar) can react to it.
    const hasUsableData =
      error?.data && Object.values(error.data).some((value) => value != null);
    if (hasUsableData) {
      return { data: error.data };
    }
    return {
      error: {
        message: error?.errors?.[0]?.message ?? error?.message ?? 'Unknown error',
        errors: error?.errors ?? [],
        original: error,
      },
    };
  }
};

export const graphqlApi = createApi({
  reducerPath: 'graphqlApi',
  baseQuery: graphqlBaseQuery,
  tagTypes: [
    'CommitteeList',
    'InProgress',
    'NextApp',
    'SystemWideMessages',
    'Applicant',
    'Reviewers',
    'Applications',
    'AdmissionsCycles',
    'Reports',
    'SearchResults',
  ],
  endpoints: (builder) => ({
    // ── Dashboard queries ──
    getCommitteeList: builder.query({
      query: () => ({ query: getCommitteeListQuery }),
      transformResponse: (response) => ({
        committees: response?.getCommitteeList?.committees,
        committeeMap: response?.getCommitteeList?.committeeMap,
      }),
      providesTags: ['CommitteeList'],
    }),

    getInProgress: builder.query({
      query: () => ({ query: getInProgressQuery }),
      transformResponse: (response) =>
        filterInProgressByAdminAction(response?.getInProgress),
      providesTags: ['InProgress'],
    }),

    getNextApp: builder.query({
      query: (committeeId) => ({
        query: getNextAppQuery,
        variables: { committeeId },
      }),
      transformResponse: (response, _meta, committeeId) =>
        transformGetNextApp(committeeId, response?.getNextApplication),
      providesTags: (_result, _error, committeeId) => [
        { type: 'NextApp', id: committeeId },
      ],
    }),

    getSystemWideMessages: builder.query({
      query: () => ({ query: getSystemWideMessagesQuery }),
      transformResponse: (response) => {
        const messages = response?.getSystemWideMessages?.messages;
        return {
          messages: messages?.map((msg) => msg.message) ?? [],
          messageIds: messages?.map((msg) => msg.id) ?? [],
        };
      },
      providesTags: ['SystemWideMessages'],
    }),

    // ── Dashboard mutations ──
    dismissSystemWideMessages: builder.mutation({
      query: (messageIds) => ({
        query: dismissSystemWideMessagesMutation,
        variables: { input: { messageIds } },
      }),
      invalidatesTags: ['SystemWideMessages'],
    }),

    // ── Applicant queries ──
    getApplication: builder.query({
      query: ({ applicantEmployeeId, admitTerm, committeeId }) => ({
        query: getApplication,
        variables: { applicantEmployeeId, admitTerm, committeeId },
      }),
      transformResponse: (response) => ({
        status: response?.getApplication?.status,
        data: response?.getApplication?.status?.toUpperCase() === 'SUCCESS'
          ? transformSchemaToComponent(response?.getApplication?.data)
          : undefined,
        error: response?.getApplication?.error,
      }),
      providesTags: (_result, _error, { applicantEmployeeId }) => [
        { type: 'Applicant', id: applicantEmployeeId },
      ],
    }),

    getApplicationReadOnly: builder.query({
      query: ({ applicantEmployeeId, admitTerm, committeeId }) => ({
        query: getApplicationReadOnlyQuery,
        variables: { applicantEmployeeId, admitTerm, committeeId },
      }),
      transformResponse: (response) => ({
        status: response?.getApplicationReadOnly?.status,
        data: response?.getApplicationReadOnly?.status?.toUpperCase() === 'SUCCESS'
          ? transformSchemaToComponent(response?.getApplicationReadOnly?.data)
          : undefined,
        error: response?.getApplicationReadOnly?.error,
      }),
      providesTags: (_result, _error, { applicantEmployeeId }) => [
        { type: 'Applicant', id: applicantEmployeeId },
      ],
    }),

    getTraining: builder.query({
      query: ({ applicantEmployeeId, admitTerm, committeeId }) => ({
        query: getTraining,
        variables: { applicantEmployeeId, admitTerm, committeeId },
      }),
      transformResponse: (response) => ({
        status: response?.getTraining?.status,
        data: response?.getTraining?.status?.toUpperCase() === 'SUCCESS'
          ? transformSchemaToComponent(response?.getTraining?.data)
          : undefined,
        error: response?.getTraining?.error,
      }),
      providesTags: (_result, _error, { applicantEmployeeId }) => [
        { type: 'Applicant', id: applicantEmployeeId },
      ],
    }),

    searchApplicationDocuments: builder.query({
      query: ({ applicantEmployeeId, admitTerm, committeeId, isTraining = false }) => ({
        query: searchApplicationDocuments,
        variables: { applicantEmployeeId, admitTerm, committeeId, isTraining },
      }),
      transformResponse: (response) => ({
        status: response?.searchApplicationDocuments?.status,
        data: response?.searchApplicationDocuments?.status?.toUpperCase() === 'SUCCESS'
          ? transformDocumentList(response?.searchApplicationDocuments?.data)
          : [],
        error: response?.searchApplicationDocuments?.error,
      }),
      providesTags: (_result, _error, { applicantEmployeeId }) => [
        { type: 'Applicant', id: `docs-${applicantEmployeeId}` },
      ],
    }),

    searchApplicationDocumentsReadOnly: builder.query({
      query: ({ applicantEmployeeId, committeeId }) => ({
        query: searchApplicationDocumentsReadOnlyQuery,
        variables: { applicantEmployeeId, committeeId },
      }),
      transformResponse: (response) => ({
        status: response?.searchApplicationDocumentsReadOnly?.status,
        data: response?.searchApplicationDocumentsReadOnly?.status?.toUpperCase() === 'SUCCESS'
          ? transformDocumentList(response?.searchApplicationDocumentsReadOnly?.data)
          : [],
        error: response?.searchApplicationDocumentsReadOnly?.error,
      }),
      providesTags: (_result, _error, { applicantEmployeeId }) => [
        { type: 'Applicant', id: `docs-${applicantEmployeeId}` },
      ],
    }),

    getApplicationDocument: builder.query({
      query: ({ documentId, documentName, admitTerm, applicantEmployeeId, committeeId }) => ({
        query: getApplicationDocument,
        variables: { documentId, documentName, admitTerm, applicantEmployeeId, committeeId },
      }),
      transformResponse: (response) => response?.getApplicationDocument,
    }),

    getApplicationDocumentReadOnly: builder.query({
      query: ({ documentId, documentName, applicantEmployeeId }) => ({
        query: getApplicationDocumentReadOnly,
        variables: { documentId, documentName, applicantEmployeeId },
      }),
      transformResponse: (response) => response?.getApplicationDocumentReadOnly,
    }),

    getApplicationTrainingDocument: builder.query({
      query: ({ documentId, documentName, admitTerm, applicantEmployeeId, committeeId }) => ({
        query: getApplicationTrainingDocument,
        variables: { documentId, documentName, admitTerm, applicantEmployeeId, committeeId },
      }),
      transformResponse: (response) => response?.getApplicationTrainingDocument,
    }),

    getCommitteeReviewers: builder.query({
      query: ({ committeeId, createdDate = 'CURRENT', includeInactive = false }) => ({
        query: getCommitteeReviewers,
        variables: { committeeId, createdDate, includeInactive },
      }),
      transformResponse: (response) =>
        transformReviewer(response?.getCommitteeReviewersFromDB),
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'Reviewers', id: committeeId },
      ],
    }),

    getExpectedRecommendation: builder.query({
      query: ({ applicantEmployeeId, committeeId, term }) => ({
        query: getExpectedRecommendationQuery,
        variables: { applicantEmployeeId, committeeId, term },
      }),
      transformResponse: (response) => response?.getExpectedRecommendation,
      providesTags: (_result, _error, { applicantEmployeeId }) => [
        { type: 'Applicant', id: `expected-rec-${applicantEmployeeId}` },
      ],
    }),

    getTrainingConfig: builder.query({
      query: ({ applicantEmployeeId, committeeId, term }) => ({
        query: getTrainingConfigQuery,
        variables: { applicantEmployeeId, committeeId, term },
      }),
      transformResponse: (response) => response?.getTrainingConfig,
      providesTags: (_result, _error, { applicantEmployeeId }) => [
        { type: 'Applicant', id: `training-config-${applicantEmployeeId}` },
      ],
    }),

    // ── Applicant mutations ──
    saveReview: builder.mutation({
      query: (input) => ({
        query: saveReviewMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.saveReview,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    submitReview: builder.mutation({
      query: (input) => ({
        query: submitReviewMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.submitReview,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
        'NextApp',
        'InProgress',
      ],
    }),

    submitTraining: builder.mutation({
      query: (input) => ({
        query: submitTrainingMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.submitTraining,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    overrideRecommendation: builder.mutation({
      query: (input) => ({
        query: overrideRecommendationMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.submitOverride,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    submitBulkAdminRecommendation: builder.mutation({
      query: (input) => ({
        query: submitBulkAdminRecommendationMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.submitBulkAdminRecommendation,
    }),

    getCommitteeRecommendationOptions: builder.query({
      query: (committeeId) => ({
        query: getCommitteeRecommendationOptionsQuery,
        variables: { committeeId },
      }),
      transformResponse: (response) => response?.getCommitteeRecommendationOptions,
    }),

    createTraining: builder.mutation({
      query: (input) => ({
        query: createTrainingMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.createTraining,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
        { type: 'Applicant', id: `training-config-${input.applicantEmployeeId}` },
      ],
    }),

    updatePriority: builder.mutation({
      query: (input) => ({
        query: updatePriorityMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.updateApplicationPriority,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    editTraining: builder.mutation({
      query: (input) => ({
        query: editTrainingMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.editTraining,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    unassignTrainee: builder.mutation({
      query: (input) => ({
        query: unassignTraineeMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.unassignTrainee,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    assignApplication: builder.mutation({
      query: (input) => ({
        query: assignApplicationMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.assignApplication,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
        { type: 'Reviewers', id: input.committeeId },
      ],
    }),

    unassignApplication: builder.mutation({
      query: (input) => ({
        query: unassignApplicationMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.unassignApplication,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
        { type: 'Reviewers', id: input.committeeId },
      ],
    }),

    reportConflict: builder.mutation({
      query: (input) => ({
        query: reportConflictMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.reportConflict,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    restartEvaluation: builder.mutation({
      query: (input) => ({
        query: restartEvaluationMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.restartEvaluation,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    notifyAdmin: builder.mutation({
      query: (input) => ({
        query: notifyAdminMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.notifyAdmin,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    reassignApplication: builder.mutation({
      query: (input) => ({
        query: reassignApplicationMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.reassignApplication,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
        { type: 'Reviewers', id: input.committeeId },
      ],
    }),

    clearNotifyAdmin: builder.mutation({
      query: (input) => ({
        query: clearNotifyAdminMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.clearNotifyAdmin,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    holdForExport: builder.mutation({
      query: (input) => ({
        query: holdForExportMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.holdForExport,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    holdForAdmin: builder.mutation({
      query: (input) => ({
        query: holdForAdminMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.holdForAdmin,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Applicant', id: input.applicantEmployeeId },
      ],
    }),

    // ── ManageApplications queries ──
    getManageApplicationsData: builder.query({
      query: () => ({ query: getManageApplicationsDataQuery }),
      transformResponse: (response) => {
        const data = response?.getManageApplicationsData;
        if (data && typeof data.filterOptionsAsJsonArr === 'string') {
          const filterOptions = JSON.parse(data.filterOptionsAsJsonArr) ?? [];
          data.filterOptionsOriginal = filterOptions;
          data.filterOptionsCustom = [];
          data.filterOptions = [...filterOptions];
        }
        return data;
      },
      providesTags: ['Applications'],
    }),

    listAssignableApplications: builder.query({
      query: ({ reviewerEmployeeId, committeeId, filtersAsJsonArr, lastEvaluatedKey, limit }) => ({
        query: listAssignableApplicationsQuery,
        variables: { reviewerEmployeeId, committeeId, filtersAsJsonArr, lastEvaluatedKey, limit },
      }),
      transformResponse: (response) => response?.listAssignableApplications,
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'Applications', id: `assignable-${committeeId}` },
      ],
    }),

    listAssignedApplications: builder.query({
      query: ({ reviewerEmployeeId, committeeId, countsOnly = false }) => ({
        query: listAssignedApplicationsQuery,
        variables: { reviewerEmployeeId, committeeId, countsOnly },
      }),
      transformResponse: (response) => response?.listAssignedApplications,
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'Applications', id: `assigned-${committeeId}` },
      ],
    }),

    getReviewerDetailsByCommittee: builder.query({
      query: ({ committeeId }) => ({
        query: getReviewerDetailsByCommitteeQuery,
        variables: { committeeId },
      }),
      transformResponse: (response) => response?.getReviewerDetailsByCommittee,
    }),

    getCommitteeDetails: builder.query({
      query: ({ committeeId }) => ({
        query: getCommitteeDetailsQuery,
        variables: { committeeId },
      }),
      transformResponse: (response) => response?.getCommitteeDetails,
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'CommitteeList', id: `details-${committeeId}` },
      ],
    }),

    // ── AssignDrawer queries ──
    getReviewerDetailsById: builder.query({
      query: ({ reviewerEmployeeId }) => ({
        query: getReviewerDetailsByIdQuery,
        variables: { reviewerEmployeeId },
      }),
      transformResponse: (response) => response?.getReviewerDetailsById,
      providesTags: (_result, _error, { reviewerEmployeeId }) => [
        { type: 'Reviewers', id: reviewerEmployeeId },
      ],
    }),

    // ── ManageApplications mutations ──
    manualBatchAssignApplications: builder.mutation({
      query: (input) => ({
        query: assignApplicationsMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.manualBatchAssignApplicationsV2,
      invalidatesTags: ['Applications', 'Reviewers', 'CommitteeList'],
    }),

    manualBatchUnassignApplications: builder.mutation({
      query: (input) => ({
        query: unassignApplicationsMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.manualBatchUnassignApplications,
      invalidatesTags: ['Applications', 'Reviewers', 'CommitteeList'],
    }),

    prioritizeApplicationsBulk: builder.mutation({
      query: (input) => ({
        query: prioritizeApplicationsBulkMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.updateApplicationPriorityBatch,
      invalidatesTags: ['Applications', 'Reviewers', 'CommitteeList'],
    }),

    // ── ManageReviewer queries ──
    getManageReviewer: builder.query({
      query: ({ reviewerEmployeeId }) => ({
        query: getManageReviewerQuery,
        variables: { reviewerEmployeeId },
      }),
      transformResponse: (response) => response?.getManageReviewer,
      providesTags: (_result, _error, { reviewerEmployeeId }) => [
        { type: 'Reviewers', id: `manage-${reviewerEmployeeId}` },
      ],
    }),

    // ── ManageReviewer mutations ──
    updateManageReviewer: builder.mutation({
      query: (input) => ({
        query: updateManageReviewerMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.updateManageReviewer,
      invalidatesTags: (_result, _error, input) => {
        const tags = ['Reviewers'];
        if (Array.isArray(input)) {
          input.forEach((r) => {
            if (r.reviewerEmployeeId) {
              tags.push({ type: 'Reviewers', id: `manage-${r.reviewerEmployeeId}` });
            }
          });
        }
        return tags;
      },
    }),

    // ── ManageReviewersBulkAssign queries ──
    getBulkAssignCommitteeReviewers: builder.query({
      query: ({ committeeId, createdDate }) => ({
        query: getCommitteeReviewersFromDBQuery,
        variables: { committeeId, createdDate },
      }),
      transformResponse: (response) => response?.getCommitteeReviewersFromDB,
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'Reviewers', id: `bulk-${committeeId}` },
      ],
    }),

    // ── ManageReviewersBulkAssign mutations ──
    bulkAssignReviewers: builder.mutation({
      query: (input) => ({
        query: bulkAssignReviewersMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.updateManageReviewer,
      invalidatesTags: ['Reviewers'],
    }),

    // ── ManageActiveReviewers queries ──
    getCommitteeReviewersActive: builder.query({
      query: ({ committeeId, createdDate, includeInactive = true }) => ({
        query: getActiveReviewersQuery,
        variables: { committeeId, createdDate, includeInactive },
      }),
      transformResponse: (response) => response?.getCommitteeReviewersFromDB,
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'Reviewers', id: `active-${committeeId}` },
      ],
    }),

    // ── ManageActiveReviewers mutations ──
    activateReviewers: builder.mutation({
      query: (input) => ({
        query: activateReviewersMutation,
        variables: { input },
      }),
      transformResponse: (response) => response?.activateReviewers,
      invalidatesTags: (_result, _error, input) => [
        { type: 'Reviewers', id: `active-${input?.committeeId}` },
        'Reviewers',
      ],
    }),

    // ── ManageAdmissionsCycles queries ──
    listAdmissionsCycles: builder.query({
      query: () => ({ query: listAdmissionsCyclesQuery }),
      transformResponse: (response) => response?.listAdmissionsCycles,
      providesTags: ['AdmissionsCycles'],
    }),

    // ── ManageAdmissionsCycles mutations ──
    saveNewAdmissionsCycle: builder.mutation({
      query: ({ terms }) => ({
        query: saveNewAdmissionsCycleMutation,
        variables: { input: { terms } },
      }),
      transformResponse: (response) => response?.saveNewAdmissionsCycle,
      invalidatesTags: ['AdmissionsCycles'],
    }),

    // ── Navbar queries ──
    navbarSearchByEmployeeId: builder.query({
      query: ({ applicantEmployeeId }) => ({
        query: getSearchResultsQuery,
        variables: { applicantEmployeeId },
      }),
      transformResponse: (response) => response?.getSearchResults,
      providesTags: ['SearchResults'],
    }),

    navbarSearchByNameNumber: builder.query({
      query: ({ searchString }) => ({
        query: searchCommitteeReviewers,
        variables: { searchString },
      }),
      transformResponse: (response) => response?.searchCommitteeReviewers,
      providesTags: ['SearchResults'],
    }),

    // ── AuditLookup queries ──
    getUltimateSearchResults: builder.query({
      query: ({ applicantEmployeeId }) => ({
        query: getSearchResultsQuery,
        variables: { applicantEmployeeId: applicantEmployeeId.trim() },
      }),
      transformResponse: (response) => response?.getSearchResults,
      providesTags: ['SearchResults'],
    }),

    ultimateSearchByNameNumber: builder.query({
      query: ({ searchString }) => ({
        query: searchCommitteeReviewers,
        variables: { searchString },
      }),
      transformResponse: (response) => response?.searchCommitteeReviewers,
      providesTags: ['SearchResults'],
    }),

    // ── ReportsDash queries ──
    listCustomReports: builder.query({
      query: () => ({ query: listCustomReportsQuery }),
      transformResponse: (response) => response?.listCustomReports,
      providesTags: ['Reports'],
    }),

    listCustomFilters: builder.query({
      query: ({ committeeId }) => ({
        query: listCustomFiltersQuery,
        variables: { committeeId },
      }),
      transformResponse: (response) => response?.getAthenaCustomFilters,
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'Reports', id: `filters-${committeeId}` },
      ],
    }),

    // ── ReportAthenaCustom queries ──
    getAthenaReport: builder.query({
      query: ({ reportId, committeeId, filters, queryExecutionId, nextToken, fullDownloadData }) => ({
        query: getCustomReportDataQuery,
        variables: { reportId, committeeId, filters, queryExecutionId, nextToken, fullDownloadData },
      }),
      transformResponse: (response) => response?.getAthenaReport,
      providesTags: ['Reports'],
    }),

    // ── ReportsExports queries ──
    listExportsForCommitteeId: builder.query({
      query: ({ committeeId }) => ({
        query: listExportsForCommitteeIdQuery,
        variables: { committeeId },
      }),
      transformResponse: (response) => response?.listExportsForCommitteeId,
      providesTags: (_result, _error, { committeeId }) => [
        { type: 'Reports', id: `exports-${committeeId}` },
      ],
    }),

    listExportsForCommitteeIds: builder.query({
      query: ({ committeeIds }) => ({
        query: listExportsForCommitteeIdsQuery,
        variables: { committeeIds },
      }),
      transformResponse: (response) => response?.listExportsForCommitteeIds,
      providesTags: (_result, _error, { committeeIds }) =>
        committeeIds.map(id => ({ type: 'Reports', id: `exports-${id}` })),
    }),

    getDownloadableLinkForPaths: builder.query({
      query: ({ filenames, committeeId, recommendationKey, executionIds }) => ({
        query: getDownloadableLinkForPathsQuery,
        variables: { filenames, committeeId, recommendationKey, executionIds },
      }),
      transformResponse: (response) => response?.getDownloadableLinkForPaths,
    }),

    // ── ReportsExports mutations ──
    saveExportTaskComplete: builder.mutation({
      query: ({ executionId, recommendationKey, isComplete }) => ({
        query: saveExportTaskCompleteMutation,
        variables: { input: { executionId, recommendationKey, isComplete } },
      }),
      transformResponse: (response) => response?.saveExportTaskComplete,
      invalidatesTags: ['Reports'],
    }),

    // ── ReportAthenaCustom mutations ──
    saveReviewNotesAddressed: builder.mutation({
      query: ({ applicantEmployeeId, committeeId, term, readerEmployeeId, addressed }) => ({
        query: saveReviewNotesAddressedMutation,
        variables: { input: { applicantEmployeeId, committeeId, term, readerEmployeeId, addressed } },
      }),
      transformResponse: (response) => response?.saveReviewNotesAddressed,
    }),

    // ── ReportAthenaCustom queries ──
    getReviewNotesAddressedStatus: builder.query({
      query: ({ committeeId, keys }) => ({
        query: getReviewNotesAddressedStatusQuery,
        variables: { committeeId, keys: JSON.stringify(keys) },
      }),
      transformResponse: (response) => response?.getReviewNotesAddressedStatus,
    }),

    // ── ReportAdminReviews mutations ──
    exportAdminReviewsReports: builder.mutation({
      query: ({ committeeId, applicantEmployeeIdsAndTerms }) => ({
        query: exportAdminReviewsReportApplicationsMutation,
        variables: { input: { committeeId, applicantEmployeeIdsAndTerms } },
      }),
      transformResponse: (response) => response?.exportAdminReviewsReports,
      invalidatesTags: ['Reports'],
    }),

    // ── ReportRecommendations mutations ──
    exportRecommendationsReports: builder.mutation({
      query: ({ committeeId, applicantEmployeeIdsAndTerms }) => ({
        query: exportRecommendationsReportsMutation,
        variables: { input: { committeeId, applicantEmployeeIdsAndTerms } },
      }),
      transformResponse: (response) => response?.exportRecommendationsReports,
      invalidatesTags: ['Reports'],
    }),

    // ── OpenSearch search queries ──
    searchReport: builder.query({
      query: (variables) => ({
        query: searchReportQuery,
        variables,
      }),
      transformResponse: (response) => response?.searchReport,
    }),

    resolveSelectedIds: builder.query({
      query: (variables) => ({
        query: resolveSelectedIdsQuery,
        variables,
      }),
      transformResponse: (response) => response?.resolveSelectedIds,
    }),

    exportSearchReport: builder.mutation({
      query: (variables) => ({
        query: exportSearchReportMutation,
        variables,
      }),
      transformResponse: (response) => response?.exportSearchReport,
    }),
  }),
});

export const {
  // Dashboard hooks
  useGetCommitteeListQuery,
  useGetInProgressQuery,
  useGetNextAppQuery,
  useGetSystemWideMessagesQuery,
  useDismissSystemWideMessagesMutation,
  // Applicant query hooks
  useGetApplicationQuery,
  useGetApplicationReadOnlyQuery,
  useGetTrainingQuery,
  useSearchApplicationDocumentsQuery,
  useSearchApplicationDocumentsReadOnlyQuery,
  useGetApplicationDocumentQuery,
  useGetApplicationDocumentReadOnlyQuery,
  useGetApplicationTrainingDocumentQuery,
  useGetCommitteeReviewersQuery,
  useGetExpectedRecommendationQuery,
  useGetTrainingConfigQuery,
  // Applicant mutation hooks
  useSaveReviewMutation,
  useSubmitReviewMutation,
  useSubmitTrainingMutation,
  useOverrideRecommendationMutation,
  useSubmitBulkAdminRecommendationMutation,
  useGetCommitteeRecommendationOptionsQuery,
  useCreateTrainingMutation,
  useUpdatePriorityMutation,
  useEditTrainingMutation,
  useUnassignTraineeMutation,
  useAssignApplicationMutation,
  useUnassignApplicationMutation,
  useReportConflictMutation,
  useRestartEvaluationMutation,
  useNotifyAdminMutation,
  useReassignApplicationMutation,
  useClearNotifyAdminMutation,
  useHoldForExportMutation,
  useHoldForAdminMutation,
  // ManageApplications query hooks
  useGetManageApplicationsDataQuery,
  useListAssignableApplicationsQuery,
  useListAssignedApplicationsQuery,
  useGetReviewerDetailsByCommitteeQuery,
  useGetCommitteeDetailsQuery,
  // AssignDrawer query hooks
  useGetReviewerDetailsByIdQuery,
  // ManageApplications mutation hooks
  useManualBatchAssignApplicationsMutation,
  useManualBatchUnassignApplicationsMutation,
  usePrioritizeApplicationsBulkMutation,
  // ManageReviewer hooks
  useGetManageReviewerQuery,
  useUpdateManageReviewerMutation,
  // ManageReviewersBulkAssign hooks
  useGetBulkAssignCommitteeReviewersQuery,
  useBulkAssignReviewersMutation,
  // ManageActiveReviewers hooks
  useGetCommitteeReviewersActiveQuery,
  useActivateReviewersMutation,
  // ManageAdmissionsCycles hooks
  useListAdmissionsCyclesQuery,
  useSaveNewAdmissionsCycleMutation,
  // Navbar hooks
  useNavbarSearchByEmployeeIdQuery,
  useNavbarSearchByNameNumberQuery,
  // AuditLookup hooks
  useGetUltimateSearchResultsQuery,
  useUltimateSearchByNameNumberQuery,
  // Reports hooks
  useListCustomReportsQuery,
  useListCustomFiltersQuery,
  useGetAthenaReportQuery,
  useListExportsForCommitteeIdQuery,
  useListExportsForCommitteeIdsQuery,
  useGetDownloadableLinkForPathsQuery,
  useSaveExportTaskCompleteMutation,
  useSaveReviewNotesAddressedMutation,
  useLazyGetReviewNotesAddressedStatusQuery,
  useExportAdminReviewsReportsMutation,
  useExportRecommendationsReportsMutation,
  // Lazy query hooks for document downloads
  useLazyGetApplicationDocumentQuery,
  useLazyGetApplicationDocumentReadOnlyQuery,
  useLazyGetApplicationTrainingDocumentQuery,
  // Lazy query hooks for reports
  useLazyListCustomFiltersQuery,
  useLazyGetAthenaReportQuery,
  // Lazy query hooks for exports
  useLazyGetDownloadableLinkForPathsQuery,
  // OpenSearch search hooks
  // useSearchReportQuery,
  useLazySearchReportQuery,
  useLazyResolveSelectedIdsQuery,
  useExportSearchReportMutation,
} = graphqlApi;
